import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import Modal from 'components/ui/Modal';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { GetLedger } from 'modules/coins/api';
import { LedgerRow } from './LedgerRow';

interface TransactionHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

const HISTORY_LIMIT = 50;

/** Full coin-ledger history — every coin credited and spent. */
export function TransactionHistoryModal({ open, onClose }: TransactionHistoryModalProps) {
  const { t } = useTranslation();

  const { data: ledger, isLoading } = useQuery({
    queryKey: ['coins', 'ledger', 'history'],
    queryFn: async () => (await GetLedger(HISTORY_LIMIT)).data,
    enabled: open,
  });

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t('transaction_history', 'Transaction history')}
      description={t('every_coin_credited_and_spent', 'Every coin credited and spent')}
      size="md"
    >
      {isLoading ? (
        <div className="flex flex-col" aria-hidden>
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-3 border-b border-border py-3 last:border-0">
              <Skeleton className="h-4 w-4 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-3.5 w-20" />
            </div>
          ))}
        </div>
      ) : ledger && ledger.length > 0 ? (
        <div className="max-h-[62vh] overflow-auto">
          {ledger.map((entry) => (
            <LedgerRow key={entry.id} entry={entry} showBalance divided />
          ))}
        </div>
      ) : (
        <EmptyState icon={<Coins />} title={t('no_coin_activity', 'No coin activity yet.')} className="py-10" />
      )}
    </Modal>
  );
}

export default TransactionHistoryModal;
